import React from 'react';
import { unified } from 'unified';
import rehypeParse from 'rehype-parse';
import rehypeReact from 'rehype-react';
import { renderTextWithFootnotesReferencesV2 } from '../app/feature-service-types';
import {
  Headline,
  Text,
  Divider,
  TextList,
  TextListItem,
  TextLink,
  RichText,
} from '@oneaudi/unified-web-components';
import * as prod from 'react/jsx-runtime';
import { getValidLinkAriaLabel, processFootnoteHTML } from './ariaLabelHelper';

import type { Options } from 'rehype-react';
import type { ReactElement, ReactNode } from 'react';
import styled from 'styled-components';
import { SYS_SPACE_RELATIVE_LG } from '@oneaudi/unified-web-common';

const StyledHeadline = styled(Headline)`
  sup {
    color: inherit;
    font-size: 0.583em;
  }
`;

interface Props {
  style?: { [key: string]: string };
  href?: string;
  target?: string;
  title?: string;
  ariaLabel?: string;
}

const StyledUnorderedList = styled(TextList)`
  li {
    ::before {
      content: '›';
      position: absolute;
      left: -6px;
    }
  }
`;

const StyledOrderedList = styled(TextList)``;
const isHtml = /<[a-z/][\s\S]*>/i;
const defaultOptions = { Fragment: prod.Fragment, jsx: prod.jsx, jsxs: prod.jsxs };
const rehypeReactOptions = {
  ...defaultOptions,
  components: {
    h1(props: React.PropsWithChildren) {
      return (
        <StyledHeadline
          tag="h1"
          variant={{
            type: 'headline',
            order: '1',
            weight: 'normal',
          }}
          margin={[0, 0, 'var(--spacing-relative-md)', 0]}
        >
          {processChildren(props.children)}
        </StyledHeadline>
      );
    },
    h2(props: React.PropsWithChildren) {
      return (
        <StyledHeadline
          tag="h2"
          variant={{
            type: 'headline',
            order: '2',
            weight: 'normal',
          }}
          margin={[0, 0, 'var(--spacing-relative-md)', 0]}
        >
          {processChildren(props.children)}
        </StyledHeadline>
      );
    },
    h3(props: React.PropsWithChildren) {
      return (
        <StyledHeadline
          tag="h3"
          variant={{
            type: 'headline',
            order: '3',
            weight: 'normal',
          }}
          margin={[0, 0, 'var(--spacing-relative-md)', 0]}
        >
          {processChildren(props.children)}
        </StyledHeadline>
      );
    },
    h4(props: React.PropsWithChildren) {
      return (
        <StyledHeadline
          tag="h4"
          variant={{
            type: 'headline',
            order: '4',
            weight: 'normal',
          }}
          margin={[0, 0, 'var(--spacing-relative-md)', 0]}
        >
          {processChildren(props.children)}
        </StyledHeadline>
      );
    },
    h5(props: React.PropsWithChildren) {
      return (
        <StyledHeadline
          tag="h5"
          variant={{
            type: 'headline',
            order: '4',
            weight: 'normal',
          }}
          margin={[0, 0, 'var(--spacing-relative-md)', 0]}
        >
          {processChildren(props.children)}
        </StyledHeadline>
      );
    },
    h6(props: React.PropsWithChildren) {
      return (
        <StyledHeadline
          tag="h6"
          variant={{
            type: 'headline',
            order: '4',
            weight: 'normal',
          }}
          margin={[0, 0, 'var(--spacing-relative-md)', 0]}
        >
          {processChildren(props.children)}
        </StyledHeadline>
      );
    },
    p(props: React.PropsWithChildren) {
      return (
        <Text
          tag="p"
          variant={{
            type: 'copy',
            order: '1',
            style: 'normal',
          }}
          margin={[0, 0, 'var(--spacing-relative-md)', 0]}
        >
          {processChildren(props.children)}
        </Text>
      );
    },
    div(props: React.PropsWithChildren) {
      return (
        <Text
          tag="div"
          variant={{
            type: 'copy',
            order: '1',
            style: 'normal',
          }}
        >
          {processChildren(props.children)}
        </Text>
      );
    },
    span(props: React.PropsWithChildren) {
      return (
        <Text
          tag="span"
          variant={{
            type: 'copy',
            order: '1',
            style: 'normal',
          }}
        >
          {processChildren(props.children)}
        </Text>
      );
    },
    ul(props: React.PropsWithChildren) {
      return (
        <StyledUnorderedList
          variant="bullet"
          margin={[0, 0, SYS_SPACE_RELATIVE_LG, 'var(--spacing-relative-md)']}
        >
          {processChildren(props.children)}
        </StyledUnorderedList>
      );
    },
    ol(props: React.PropsWithChildren) {
      return (
        <StyledOrderedList
          variant="decimal"
          margin={[0, 0, 'var(--spacing-relative-md)', 'var(--spacing-relative-md)']}
        >
          {processChildren(props.children)}
        </StyledOrderedList>
      );
    },
    li(props: React.PropsWithChildren) {
      return <TextListItem>{processChildren(props.children)}</TextListItem>;
    },
    hr() {
      return (
        <Divider margin={['var(--spacing-relative-2xl)', 0, 'var(--spacing-relative-2xl)', 0]} />
      );
    },
    blockquote({ children, ...props }: React.PropsWithChildren) {
      return (
        <blockquote {...props}>
          <Text
            tag="p"
            variant={{
              type: 'copy',
              order: '1',
              style: 'normal',
            }}
          >
            {processChildren(children)}
          </Text>
        </blockquote>
      );
    },
    pre(props: React.PropsWithChildren) {
      return (
        <Text
          tag="span"
          variant={{
            type: 'copy',
            order: '1',
            style: 'normal',
          }}
          margin={[0, 0, 'var(--spacing-relative-md)', 0]}
        >
          <pre>{processChildren(props.children)}</pre>
        </Text>
      );
    },
    a({ children, ...props }: React.PropsWithChildren) {
      const { href, target, title } = props as Props;
      const linkText = typeof children === 'string' ? children : '';

      // Check if this is a footnote link (href starts with #)
      const isFootnoteLink = href && href.startsWith('#');
      let validAriaLabel: string;

      if (isFootnoteLink) {
        // For footnote links, generate appropriate aria-label
        if (linkText.trim()) {
          validAriaLabel = `Footnote: ${linkText.trim()}`;
        } else {
          const footnoteId = href.substring(1);
          validAriaLabel = `Footnote reference ${footnoteId}`;
        }
      } else {
        // For regular links, use the original logic
        validAriaLabel = getValidLinkAriaLabel(title, linkText, href);
      }

      return (
        <TextLink
          href={href || ''}
          size="md"
          variant="inline"
          target={target || ''}
          aria-label={validAriaLabel}
          title={title}
        >
          {processChildren(children)}
        </TextLink>
      );
    },
    strong({ children }: React.PropsWithChildren) {
      return (
        <Text
          tag="span"
          variant={{
            type: 'copy',
            order: '1',
            style: 'bold',
          }}
        >
          {processChildren(children)}
        </Text>
      );
    },
  },
};
const processChildren = (children: string | ReactNode | ReactElement[]) => {
  if (typeof children === 'string') {
    return processChild(children);
  }
  if (typeof children === 'string' && isHtml.test(children)) {
    return <RichText markdown={children} />;
  }
  if (Array.isArray(children)) {
    return children.map((child) => processChild(child as unknown as string));
  }
  return children;
};
const processChild = (value: string) => {
  if (typeof value === 'string' && value.match(/({ft_[a-z0-9-_]*})/gi)) {
    const footnoteContent = renderTextWithFootnotesReferencesV2(value);
    return processFootnoteHTML(footnoteContent);
  }
  if (typeof value === 'string' && isHtml.test(value)) {
    return <RichText markdown={value} />;
  }
  return value;
};

export const convertHtmlToReact = (html: string) => {
  const file = unified()
    .use(rehypeParse, {
      fragment: true,
    })
    .use(rehypeReact, rehypeReactOptions as unknown as Options)
    .processSync(html);

  return file.result;
};
